/**
 * Admin Auth Service
 * 관리자 인증 비즈니스 로직
 */

import { AdminAuthRepository } from '../repositories';
import {
  AdminUser,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminPasswordChangeRequest,
  CreateOperatorRequest,
  UpdateProfileRequest,
} from '../models';

// Alias for backward compatibility
type PasswordChangeRequest = AdminPasswordChangeRequest;

export class AdminAuthService {
  constructor(private repository: AdminAuthRepository) {}

  /**
   * 로그인 처리
   */
  async login(email: string, password: string): Promise<AdminLoginResponse> {
    // 입력 검증
    if (!email || !password) {
      throw new Error('이메일과 비밀번호를 입력해주세요.');
    }

    if (!this.isValidEmail(email)) {
      throw new Error('올바른 이메일 형식이 아닙니다.');
    }

    // [2026-01-05] 중요: API 호출 **전에** 이전 세션 데이터 완전 정리
    // 이전 세션이 남아있으면 잘못된 사용자 정보가 반환될 수 있음
    console.log('🧹 Admin 로그인 전 세션 정리 시작...');
    localStorage.removeItem('user-storage');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('auth_access_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    // Supabase 관련 캐시도 정리
    localStorage.removeItem('sb-lgebgnnhtvoirbxjvfok-auth-token');
    // sessionStorage도 정리
    sessionStorage.clear();
    console.log('🧹 세션 정리 완료');

    const request: AdminLoginRequest = { email, password };
    console.log('🔐 Admin 로그인 요청:', email);
    const response = await this.repository.login(request);

    // API 응답에서 사용자 정보 추출
    const user = (response as any).user || response.admin_user;

    // 디버깅: API 응답 확인
    console.log('📋 API 응답 user:', user);
    console.log('📋 API 응답 user.email:', user?.email);
    console.log('📋 API 응답 user.name:', user?.name);

    // 중요: API 응답의 이메일이 요청한 이메일과 일치하는지 확인
    if (user && user.email && user.email.toLowerCase() !== email.toLowerCase()) {
      console.error('⚠️ 경고: 응답 이메일이 요청 이메일과 다름!', {
        requested: email,
        received: user.email,
      });
    }

    // Admin 로그인이므로 role을 항상 'admin'으로 설정
    if (user) {
      user.role = 'admin';
    }

    // 토큰 저장
    localStorage.setItem('admin_token', response.access_token);
    localStorage.setItem('accessToken', response.access_token);
    localStorage.setItem('admin_user', JSON.stringify(user));

    // userStore도 업데이트 (ProtectedRoute에서 사용)
    const { useUserStore } = await import('../../../store/userStore');
    const store = useUserStore.getState();
    store.setCurrentUser(user);

    console.log('✅ Admin 로그인 완료:', { email: user.email, name: user.name, role: user.role });

    return response;
  }

  /**
   * 로그아웃 처리
   */
  async logout(): Promise<void> {
    try {
      await this.repository.logout();
    } finally {
      // API 실패해도 로컬 토큰은 제거
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');

      // userStore도 정리 (ProtectedRoute에서 사용)
      const { useUserStore } = await import('../../../store/userStore');
      const store = useUserStore.getState();
      store.setCurrentUser(null);

      // zustand persist 데이터도 정리
      localStorage.removeItem('user-storage');
    }
  }

  /**
   * 프로필 조회
   */
  async getProfile(): Promise<AdminUser> {
    const profile = await this.repository.getProfile();

    // 로컬 스토리지 업데이트
    localStorage.setItem('admin_user', JSON.stringify(profile));

    return profile;
  }

  /**
   * 프로필 수정
   */
  async updateProfile(request: UpdateProfileRequest): Promise<AdminUser> {
    // 입력 검증
    if (request.phone && !this.isValidPhone(request.phone)) {
      throw new Error('올바른 전화번호 형식이 아닙니다.');
    }

    const updatedProfile = await this.repository.updateProfile(request);

    // 로컬 스토리지 업데이트
    localStorage.setItem('admin_user', JSON.stringify(updatedProfile));

    return updatedProfile;
  }

  /**
   * 비밀번호 변경
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    // 입력 검증
    if (!currentPassword || !newPassword) {
      throw new Error('현재 비밀번호와 새 비밀번호를 입력해주세요.');
    }

    if (!this.isValidPassword(newPassword)) {
      throw new Error(
        '비밀번호는 8자 이상, 영문/숫자/특수문자를 포함해야 합니다.'
      );
    }

    if (currentPassword === newPassword) {
      throw new Error('현재 비밀번호와 새 비밀번호가 같습니다.');
    }

    const request: PasswordChangeRequest = {
      current_password: currentPassword,
      new_password: newPassword,
    };

    await this.repository.changePassword(request);
  }

  /**
   * Operator 생성 (super_admin만 가능)
   */
  async createOperator(request: CreateOperatorRequest): Promise<AdminUser> {
    // 입력 검증
    if (!request.email || !request.password || !request.name) {
      throw new Error('필수 정보를 모두 입력해주세요.');
    }

    if (!this.isValidEmail(request.email)) {
      throw new Error('올바른 이메일 형식이 아닙니다.');
    }

    if (!this.isValidPassword(request.password)) {
      throw new Error(
        '비밀번호는 8자 이상, 영문/숫자/특수문자를 포함해야 합니다.'
      );
    }

    if (!request.service_type) {
      throw new Error('서비스 타입을 선택해주세요.');
    }

    return await this.repository.createOperator(request);
  }

  /**
   * 현재 로그인한 사용자 정보 가져오기
   */
  getCurrentUser(): AdminUser | null {
    const userJson = localStorage.getItem('admin_user');
    if (!userJson) return null;

    try {
      return JSON.parse(userJson) as AdminUser;
    } catch {
      return null;
    }
  }

  /**
   * 로그인 상태 확인
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('admin_token');
  }

  /**
   * 이메일 검증
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 전화번호 검증 (한국 형식)
   */
  private isValidPhone(phone: string): boolean {
    const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
    return phoneRegex.test(phone);
  }

  /**
   * 비밀번호 검증
   * - 8자 이상
   * - 영문, 숫자, 특수문자 포함
   */
  private isValidPassword(password: string): boolean {
    if (password.length < 8) return false;

    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return hasLetter && hasNumber && hasSpecial;
  }
}
